import React, { useContext, useEffect, useState } from 'react'

import {
  type ChatStreamModel,
  animationFrameSubagentPingScheduler,
  createChatStreamModel,
} from '@/components/chat-v2/lib/chatStreamModel'
import { ChatSocketMuxes } from '@/components/chat-v2/websockets/chatSocketMux'

const RECENT_CHAT_MODELS = 8

/**
 * The models of the last few chats, so switching back renders the thread at
 * once while the socket reconnects and resyncs the model from where it left off.
 */
export class RecentChatModels {
  private readonly models = new Map<string, ChatStreamModel>()

  get(chatKey: string): ChatStreamModel {
    const model =
      this.models.get(chatKey) ??
      createChatStreamModel({
        coalesceSubagentPings: animationFrameSubagentPingScheduler,
      })
    this.models.delete(chatKey)
    this.models.set(chatKey, model)
    for (const stale of [...this.models.keys()].slice(0, -RECENT_CHAT_MODELS)) {
      this.models.delete(stale)
    }
    return model
  }

  clear(): void {
    this.models.clear()
  }
}

/**
 * Every change before the next frame renders on that frame, which still comes
 * before it paints. One frame callback runs them all, so React renders them in
 * one commit: microtasks run between frame callbacks, and each would commit
 * on its own.
 */
export class FrameBatch {
  private readonly changes = new Set<() => void>()
  private frame = 0

  schedule(change: () => void): void {
    this.changes.add(change)
    this.frame ||= requestAnimationFrame(() => {
      this.frame = 0
      const changes = [...this.changes]
      this.changes.clear()
      for (const run of changes) run()
    })
  }

  cancel(change: () => void): void {
    this.changes.delete(change)
  }

  clear(): void {
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = 0
    this.changes.clear()
  }
}

/** A chat surface's streaming resources, shared by every chat it shows. */
export class ChatStreaming {
  readonly recentModels = new RecentChatModels()
  readonly frames = new FrameBatch()
  readonly muxes = new ChatSocketMuxes()

  clear(): void {
    this.recentModels.clear()
    this.frames.clear()
    this.muxes.clear()
  }
}

const ChatStreamingContext = React.createContext<ChatStreaming | null>(null)

interface ChatStreamingProviderProps {
  children: React.ReactNode
}

/**
 * Owns a chat surface's streaming resources. Inside another one it adds nothing,
 * so a panel that switches between chats can share one socket and one set of
 * recent models across them.
 */
export const ChatStreamingProvider: React.FC<ChatStreamingProviderProps> = ({
  children,
}) => {
  const enclosing = useContext(ChatStreamingContext)
  if (enclosing) return children
  return <OwnChatStreaming>{children}</OwnChatStreaming>
}

const OwnChatStreaming: React.FC<ChatStreamingProviderProps> = ({
  children,
}) => {
  const [streaming] = useState(() => new ChatStreaming())
  useEffect(() => () => streaming.clear(), [streaming])
  return (
    <ChatStreamingContext.Provider value={streaming}>
      {children}
    </ChatStreamingContext.Provider>
  )
}

/** The surface's chat streaming resources; null outside a chat surface, as in isolated tests. */
export function useChatStreaming(): ChatStreaming | null {
  return useContext(ChatStreamingContext)
}
